import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { cloneDeep } from "lodash";
import dayjs from "dayjs";
import { BookCheck, LoaderCircle, Search, TrendingUpDown } from "lucide-react";

import api from "apiSingleton";
import { AlertContext } from "context/providers";
import AssessmentReportLink from "components/AssessmentReportLink";
import AssessmentReport from "components/AssessmentReport";
import AppDialog from "components/AppDialog";
import ActionButton from "components/base/ActionButton";
import Paginator from "components/Paginator";
import { Input } from "components/ui/input";
import { Checkbox } from "components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import * as RootDialog from "components/ui/dialog";
import { getUserName } from "utils/helpers/users";
import {
  formatIntegrityRisk,
  getIntegrityRisk,
  getIntegrityRiskStyles,
} from "utils/helpers/assessments";
import { cn } from "utils";
import styles from "./index.module.css";

const SOURCES = {
  ROPES: "ROPES",
  CODERBYTE: "CODERBYTE",
};

const PAGE_SIZE = 5;

const DEFAULT_FILTERS = [
  { label: "Taken", key: "isTaken", value: false },
  { label: "Sent", key: "isShared", value: false },
  { label: "Ropes", key: "isRopes", value: true },
  { label: "Coderbyte", key: "isCoderbyte", value: false },
];

const CenteredHyphen = () => <p className="text-center">-</p>;

const AssessmentModal = ({ user, onClose }) => {
  const { addGraphQLAlert } = useContext(AlertContext);
  const [assessments, setAssessments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  const assessmentsData = useRef([]);
  const userSharedAssessments = useRef([]);

  const clonedUser = cloneDeep(user);

  const mapAssessments = (list) => {
    return list
      .filter(({ isClosed }) => !isClosed)
      .map((assessment) => ({
        ...assessment,
        isShared: userSharedAssessments.current.some(
          ({ testId }) => testId === assessment.testId
        ),
        takenA: clonedUser.assessments?.find(
          ({ testId }) => testId === assessment.testId
        ),
      }));
  };

  const prepareScore = (item) => {
    return `${item.finalScore || 0} (Code - ${item.codeScore || 0}, MCQ - ${
      item.multipleChoiceScore || 0
    })`;
  };

  const renderAssessments = (assessment) => {
    return (
      <div
        className="flex w-full flex-col font-inter text-sm"
        key={assessment.id}
      >
        <div className="flex justify-between gap-2">
          <p className="w-auto whitespace-nowrap">{assessment.status}</p>
          <p className="w-auto whitespace-nowrap">{prepareScore(assessment)}</p>
        </div>
      </div>
    );
  };

  const renderAssessmentReport = (assessment) => {
    if (!assessment?.resultDetails?.analysis) {
      return <CenteredHyphen />;
    }

    return (
      <RootDialog.Dialog>
        <RootDialog.DialogTrigger>
          <TrendingUpDown size={16} />
        </RootDialog.DialogTrigger>

        <RootDialog.DialogPortal>
          <RootDialog.DialogOverlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300" />

          <RootDialog.DialogContent className="z-50 flex max-h-[90vh] w-[95vw] max-w-6xl flex-col bg-slate-50 shadow-2xl md:w-[90%]">
            <div className="z-20 flex flex-shrink-0 flex-row items-center justify-between border-b border-slate-100 p-4 px-6">
              <div className="flex flex-col">
                <RootDialog.DialogTitle className="font-inter text-base font-bold uppercase tracking-wider text-slate-900">
                  Assessment Overview
                </RootDialog.DialogTitle>
                <RootDialog.DialogDescription className="mt-1 font-inter text-sm text-slate-500">
                  {assessment.testName}
                </RootDialog.DialogDescription>
              </div>
            </div>

            <AssessmentReport assessment={assessment} hit={clonedUser} />
          </RootDialog.DialogContent>
        </RootDialog.DialogPortal>
      </RootDialog.Dialog>
    );
  };

  const renderPdfResult = (assessment) => {
    if (!assessment?.pdfResultLocation) {
      return <CenteredHyphen />;
    }

    return (
      <div className="flex w-full justify-center">
        <a
          href={`https://res.cloudinary.com/${
            process.env.REACT_APP_CLOUDINARY_CLOUDNAME
          }/image/upload/v${Date.now()}/${assessment.pdfResultLocation}.pdf`}
          target="_blank"
          rel="noreferrer"
          title="PDF Result"
        >
          <BookCheck size={24} />
        </a>
      </div>
    );
  };

  const renderScore = (assessment) => {
    if (!assessment) {
      return <CenteredHyphen />;
    }

    const scoreText = prepareScore(assessment);

    return (
      <AssessmentReportLink
        assessment={assessment}
        userId={clonedUser.id}
        className={cn({ "text-blue-500": !!assessment.reportLink })}
      >
        {scoreText}
      </AssessmentReportLink>
    );
  };

  // A missing rating renders as a hyphen rather than as a reassuring value -
  // an assessment that was never rated is not the same as one rated clean.
  const renderIntegrityRisk = (assessment) => {
    const risk = getIntegrityRisk(assessment);

    if (!risk) {
      return <CenteredHyphen />;
    }

    return (
      <span
        className={cn(
          "inline-flex rounded border px-2 py-0.5 text-xs font-bold uppercase",
          getIntegrityRiskStyles(risk)
        )}
        title={formatIntegrityRisk(risk)}
      >
        {formatIntegrityRisk(risk)}
      </span>
    );
  };

  const renderTestName = ({ testName, takenA }) => {
    if (!takenA) {
      return (
        <p
          className="block max-w-[420px] whitespace-normal break-words leading-snug"
          title={testName}
        >
          {testName}
        </p>
      );
    }

    return (
      <div className={cn(styles.tooltip)}>
        <AssessmentReportLink
          assessment={takenA}
          userId={clonedUser.id}
          title={takenA.testName}
          className={cn(
            "block max-w-[420px] whitespace-normal break-words leading-snug",
            { "text-blue-500": !!takenA.reportLink }
          )}
        >
          {takenA.testName}
        </AssessmentReportLink>
        <div
          className={cn(
            styles["tooltip-content"],
            "absolute -ml-1 mt rounded-md border-2 bg-white p-4 shadow-lg z-50"
          )}
        >
          <div className="flex flex-wrap">{renderAssessments(takenA)}</div>
        </div>
      </div>
    );
  };

  const getAssessmentSource = (link) => {
    if (link.includes("ropes")) {
      return SOURCES.ROPES;
    }

    if (link.includes("coderbyte")) {
      return SOURCES.CODERBYTE;
    }

    return "-";
  };

  const handleShareAssessmentAction = async ({
    testId,
    link,
    testName,
    source,
  }) => {
    let response;
    try {
      const { data } = await api.user.shareAssessmentMutation({
        input: {
          userId: clonedUser.id,
          testName,
          testId,
          link,
          source,
        },
      });
      response = data.createSharedAssessment.message === "SUCCESS";
    } catch (err) {
      console.log("Something went wrong on send assessment", err);
      addGraphQLAlert(err);
    }

    if (!response) return;

    const assessmentsDataCopy = [...assessmentsData.current];
    const index = assessmentsDataCopy.findIndex(
      (assessment) => assessment.testId === testId
    );

    if (index !== -1) {
      assessmentsDataCopy[index] = {
        ...assessmentsDataCopy[index],
        isShared: true,
      };
      assessmentsData.current = assessmentsDataCopy;
      setAssessments(assessmentsDataCopy);
    }
  };

  const getUserSharedAssessments = async (userId) => {
    try {
      const { data } = await api.user.getSharedAssessments({
        userId,
      });
      return data.listSharedAssessmentsByUser.items;
    } catch (err) {
      console.log("Something went wrong getting user shared assessments", err);
      addGraphQLAlert(err);
    }
  };

  const getAvailableAssessments = async () => {
    try {
      const [coderbyteRes, ropesRes] = await Promise.all([
        api.common.listCoderbyteAssessments(),
        api.common.listRopesProblems(),
      ]);

      const coderbyteAssessments =
        coderbyteRes.data.listCoderbyteAssessments.map((e) => ({
          ...e,
          source: SOURCES.CODERBYTE,
        }));

      const ropesProblems = ropesRes.data.listRopesProblems.map((e) => ({
        ...e,
        source: SOURCES.ROPES,
      }));

      return [...coderbyteAssessments, ...ropesProblems];
    } catch (err) {
      console.log("Something went wrong listRopesProblems", err);
      addGraphQLAlert(err);

      return [];
    }
  };

  const initContent = async () => {
    const [availableAssessments, sharedAssessments] = await Promise.all([
      getAvailableAssessments(),
      getUserSharedAssessments(clonedUser.id),
    ]);

    userSharedAssessments.current = sharedAssessments;
    assessmentsData.current = mapAssessments(availableAssessments);

    setAssessments(assessmentsData.current);
    setIsLoading(false);
  };

  useEffect(() => {
    (async () => {
      await initContent();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterByGroups = useCallback((rows, currentFilters) => {
    return rows.filter((row) => {
      const activeKeys = currentFilters
        .filter((filter) => filter.value)
        .map((filter) => filter.key);

      const group1Keys = ["isTaken", "isShared"];
      const group2Keys = ["isRopes", "isCoderbyte"];

      const activeGroup1 = activeKeys.filter((key) => group1Keys.includes(key));
      const activeGroup2 = activeKeys.filter((key) => group2Keys.includes(key));

      const group1Match =
        activeGroup1.length === 0 ||
        activeGroup1.some((key) => Boolean(row[key]));

      const group2Match =
        activeGroup2.length === 0 ||
        activeGroup2.some((key) => Boolean(row[key]));

      return group1Match && group2Match;
    });
  }, []);

  const filteredAssessments = useMemo(() => {
    const withFlags = assessments.map((assessment) => {
      const source = assessment.source ?? getAssessmentSource(assessment.link);

      return {
        ...assessment,
        source,
        isTaken: !!assessment.takenA,
        isRopes: source === SOURCES.ROPES,
        isCoderbyte: source === SOURCES.CODERBYTE,
      };
    });

    let result = withFlags;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((assessment) =>
        (assessment.testName || "").toLowerCase().includes(term)
      );
    }

    return filterByGroups(result, filters);
  }, [assessments, searchTerm, filters, filterByGroups]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filters]);

  const pagedAssessments = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredAssessments.slice(start, start + PAGE_SIZE);
  }, [filteredAssessments, page]);

  const handleFilterChange = (key, checked) => {
    setFilters((prev) =>
      prev.map((filter) =>
        filter.key === key ? { ...filter, value: checked } : filter
      )
    );
  };

  return (
    <AppDialog
      open={!!clonedUser}
      onClose={onClose}
      header="Send Assessments"
      description={clonedUser ? `User: ${getUserName(clonedUser)}` : undefined}
      className="flex max-h-[90vh] w-[95%] max-w-6xl flex-col overflow-hidden rounded-lg"
      contentClassName="flex min-h-0 flex-1 flex-col gap-4 px-6 pb-6 pt-4"
    >
      {isLoading ? (
        <div className="flex items-center justify-center gap-x-2 p-8 font-inter text-sm text-gray-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading assessments...
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by name"
                className="w-[220px] bg-white pl-9 font-inter"
              />
            </div>

            {filters.map((filter) => (
              <label
                key={filter.key}
                htmlFor={`assessment-filter-${filter.key}`}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap font-inter text-sm"
              >
                <Checkbox
                  id={`assessment-filter-${filter.key}`}
                  checked={!!filter.value}
                  onCheckedChange={(checked) =>
                    handleFilterChange(filter.key, !!checked)
                  }
                />
                {filter.label}
              </label>
            ))}
          </div>

          {filteredAssessments.length > 0 ? (
            <div className="min-h-0 flex-1 overflow-hidden">
              <Table containerClassName="max-h-[calc(90vh-280px)] overflow-y-auto">
                <TableHeader>
                  <TableRow className="sticky top-0 z-10">
                    <TableHead className="whitespace-nowrap">
                      Completed
                    </TableHead>
                    <TableHead className="w-full">Name</TableHead>
                    <TableHead className="whitespace-nowrap">Score</TableHead>
                    <TableHead className="whitespace-nowrap">
                      Integrity
                    </TableHead>
                    <TableHead className="whitespace-nowrap">Time</TableHead>
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                    <TableHead className="whitespace-nowrap">
                      AI Report
                    </TableHead>
                    <TableHead className="whitespace-nowrap">
                      PDF Result
                    </TableHead>
                    <TableHead className="whitespace-nowrap">Source</TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedAssessments.map((assessment) => {
                    const completedAssessment = assessment.takenA;
                    const source = assessment.source;

                    return (
                      <TableRow key={assessment.testId}>
                        <TableCell className="whitespace-nowrap">
                          {completedAssessment?.assessmentCompleted ? (
                            dayjs(
                              completedAssessment.assessmentCompleted
                            ).format("MMM DD YYYY")
                          ) : (
                            <CenteredHyphen />
                          )}
                        </TableCell>
                        <TableCell className="w-full font-medium">
                          {renderTestName(assessment)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {renderScore(completedAssessment)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {renderIntegrityRisk(completedAssessment)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {completedAssessment?.timeTaken > 0 ? (
                            `${completedAssessment.timeTaken} mins`
                          ) : (
                            <CenteredHyphen />
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {completedAssessment?.status ||
                          assessment.isShared ? (
                            completedAssessment?.status || "Sent"
                          ) : (
                            <CenteredHyphen />
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex w-full justify-center">
                            {renderAssessmentReport(completedAssessment)}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {renderPdfResult(completedAssessment)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <p className="text-center capitalize" title={source}>
                            {source.toLowerCase()}
                          </p>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right">
                          <div className="flex w-full items-center justify-center gap-x-4">
                            <ActionButton
                              action={() =>
                                handleShareAssessmentAction({
                                  testId: assessment.testId,
                                  link: assessment.link,
                                  testName: assessment.testName,
                                  source,
                                })
                              }
                              title={`Send to: ${getUserName(user)}`}
                              className={cn(
                                "w-[87px] rounded-md border border-solid border-slate-300 bg-white font-inter text-center text-gray-600 shadow-none hover:text-sky-500",
                                {
                                  "opacity-70":
                                    assessment.isShared ||
                                    !!completedAssessment,
                                }
                              )}
                              text={
                                assessment.isShared || !!completedAssessment
                                  ? "Resend"
                                  : "Send"
                              }
                              loadingText="Sending..."
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Paginator
                className="mt-3"
                totalItems={filteredAssessments.length}
                pageSize={PAGE_SIZE}
                page={page}
                onPageChange={setPage}
              />
            </div>
          ) : (
            <p className="font-inter text-sm font-normal text-gray-500">
              No assessments found.
            </p>
          )}
        </>
      )}
    </AppDialog>
  );
};

AssessmentModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

AssessmentModal.defaultProps = {
  user: null,
};

export default AssessmentModal;
